<template>
  <Breadcrumb :items="breadcrumbs" />

  <div class="container-fluid my-3">
    <div class="section-header mb-3">
      <span class="section-label">تقارير الاشتراكات</span>
      <div class="section-divider"></div>
    </div>

    <Loading v-if="loading" />

    <template v-else>
      <!-- Dashboard cards -->
      <div class="row g-3 mb-4">
        <div class="col-xl-3 col-md-6" v-for="card in cards" :key="card.label">
          <div class="stat-card" :class="card.cls">
            <div class="stat-card__icon"><i :class="card.icon"></i></div>
            <div class="stat-card__body">
              <div class="stat-card__value">{{ card.value }}</div>
              <div class="stat-card__label">{{ card.label }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <!-- Revenue by plan -->
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="card-header fw-bold"><i class="pi pi-chart-bar me-1"></i> الإيراد حسب الخطة</div>
            <div class="card-body p-0">
              <table class="table table-bordered mb-0">
                <thead class="table-light"><tr><th>الخطة</th><th>العدد</th><th>الإيراد</th></tr></thead>
                <tbody>
                  <tr v-for="row in dashboard.revenue_by_plan" :key="row.plan_type">
                    <td>{{ planLabel(row.plan_type) }}</td><td>{{ row.count }}</td><td>{{ money(row.revenue) }}</td>
                  </tr>
                  <tr v-if="!dashboard.revenue_by_plan.length"><td colspan="3" class="text-center text-muted">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Revenue by product -->
        <div class="col-lg-6">
          <div class="card h-100">
            <div class="card-header fw-bold"><i class="pi pi-chart-pie me-1"></i> الإيراد حسب المنتج</div>
            <div class="card-body p-0">
              <table class="table table-bordered mb-0">
                <thead class="table-light"><tr><th>النوع</th><th>العدد</th><th>الإيراد</th></tr></thead>
                <tbody>
                  <tr v-for="row in dashboard.revenue_by_product" :key="row.type">
                    <td>{{ typeLabel(row.type) }}</td><td>{{ row.count }}</td><td>{{ money(row.revenue) }}</td>
                  </tr>
                  <tr v-if="!dashboard.revenue_by_product.length"><td colspan="3" class="text-center text-muted">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Monthly revenue -->
        <div class="col-lg-6" v-if="reports">
          <div class="card h-100">
            <div class="card-header fw-bold"><i class="pi pi-calendar me-1"></i> الإيراد الشهري (إجمالي: {{ money(reports.total_revenue) }})</div>
            <div class="card-body p-0">
              <table class="table table-bordered mb-0">
                <thead class="table-light"><tr><th>الشهر</th><th>الإيراد</th></tr></thead>
                <tbody>
                  <tr v-for="row in reports.monthly_revenue" :key="row.month"><td>{{ row.month }}</td><td>{{ money(row.revenue) }}</td></tr>
                  <tr v-if="!reports.monthly_revenue.length"><td colspan="2" class="text-center text-muted">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Most subscribed courses -->
        <div class="col-lg-6" v-if="reports">
          <div class="card h-100">
            <div class="card-header fw-bold"><i class="pi pi-book me-1"></i> الأكثر اشتراكاً — كورسات</div>
            <div class="card-body p-0">
              <table class="table table-bordered mb-0">
                <thead class="table-light"><tr><th>الكورس</th><th>عدد المشتركين</th></tr></thead>
                <tbody>
                  <tr v-for="row in reports.most_subscribed_courses" :key="'c'+row.id"><td>{{ row.label || '—' }}</td><td>{{ row.subscribers }}</td></tr>
                  <tr v-if="!reports.most_subscribed_courses.length"><td colspan="2" class="text-center text-muted">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Most subscribed years -->
        <div class="col-lg-6" v-if="reports">
          <div class="card h-100">
            <div class="card-header fw-bold"><i class="pi pi-graduation-cap me-1"></i> الأكثر اشتراكاً — سنوات دراسية</div>
            <div class="card-body p-0">
              <table class="table table-bordered mb-0">
                <thead class="table-light"><tr><th>السنة</th><th>عدد المشتركين</th></tr></thead>
                <tbody>
                  <tr v-for="row in reports.most_subscribed_years" :key="'y'+row.id"><td>{{ row.label || '—' }}</td><td>{{ row.subscribers }}</td></tr>
                  <tr v-if="!reports.most_subscribed_years.length"><td colspan="2" class="text-center text-muted">لا توجد بيانات</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Pending / expired summary -->
        <div class="col-lg-6" v-if="reports">
          <div class="card h-100">
            <div class="card-header fw-bold"><i class="pi pi-exclamation-circle me-1"></i> متابعة</div>
            <div class="card-body">
              <div class="d-flex justify-content-between py-2 border-bottom">
                <span>مدفوعات قيد المراجعة</span>
                <router-link to="/payment-requests" class="badge bg-warning text-dark text-decoration-none">{{ reports.pending_payments }}</router-link>
              </div>
              <div class="d-flex justify-content-between py-2">
                <span>اشتراكات منتهية</span>
                <router-link to="/subscriptions" class="badge bg-danger text-decoration-none">{{ reports.expired_subscriptions }}</router-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { useSubscriptionReportStore } from '@/stores/useSubscriptionReportStore.js';
import { planLabel, typeLabel, formatMoney } from '@/composables/subscriptionMeta.js';

export default {
  name: 'SubscriptionReports',
  components: { Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'تقارير الاشتراكات', path: '/subscription-reports' }],
    };
  },
  computed: {
    reportStore() { return useSubscriptionReportStore(); },
    loading() { return this.reportStore.loadingDashboard && !this.reportStore.dashboard; },
    dashboard() { return this.reportStore.dashboard || { revenue_by_plan: [], revenue_by_product: [] }; },
    reports() { return this.reportStore.reports; },
    cards() {
      const d = this.dashboard;
      return [
        { label: 'اشتراكات مفعّلة', value: d.active_subscriptions ?? 0, icon: 'pi pi-check-circle', cls: 'stat-card--green' },
        { label: 'اشتراكات منتهية', value: d.expired_subscriptions ?? 0, icon: 'pi pi-times-circle', cls: 'stat-card--red' },
        { label: 'مدفوعات قيد المراجعة', value: d.pending_payments ?? 0, icon: 'pi pi-clock', cls: 'stat-card--amber' },
        { label: 'تجديدات اليوم', value: d.todays_renewals ?? 0, icon: 'pi pi-calendar', cls: 'stat-card--blue' },
      ];
    },
  },
  methods: {
    planLabel, typeLabel,
    money: formatMoney,
  },
  mounted() {
    this.reportStore.fetchDashboard();
    this.reportStore.fetchReports();
  },
};
</script>

<style scoped>
.stat-card {
  display: flex; align-items: center; gap: 1rem;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 1.1rem 1.25rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.stat-card__icon {
  width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
  font-size: 1.4rem; color: #fff; flex-shrink: 0;
}
.stat-card__value { font-size: 1.6rem; font-weight: 800; line-height: 1; }
.stat-card__label { color: #64748b; font-size: 0.85rem; margin-top: 0.25rem; }
.stat-card--green .stat-card__icon { background: #22c55e; }
.stat-card--red .stat-card__icon { background: #ef4444; }
.stat-card--amber .stat-card__icon { background: #f59e0b; }
.stat-card--blue .stat-card__icon { background: #3b82f6; }
</style>
